/*
 * Copyright (c) Nexign, JSC, 2021-2023.
 */
/**
 * Cashback Account select component
 *
 * @author Yakov Gushchin
 * @author Julia Ignatieva
 * @author Ilya Babanov
 * @version 1.23.0
 * @since 1.12.0
 */

import { useCallback, useState } from 'react';

import debounce from 'lodash/debounce';
import { connect } from 'react-redux';
import type { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { arrowListDownLinkSmall } from '@assets/icons';
import { Select } from '@components/common';
import { EInputKind } from '@components/common/Input/Input';
import type { IOptionItemProps } from '@components/common/Select/Select';
import GTMOperations from '@modules/analytics/GoogleTagManager/GTMOperations';
import type { IGTMPushAnalyticPayload } from '@modules/analytics/GoogleTagManager/types/GTMActionTypes';
import { DEBOUNCE_DEFAULT_DELAY } from '@modules/app/appConstants';
import type { ICommonTranslation } from '@modules/internationalization/types';
import type { IPageCashbackTranslation } from '@modules/internationalization/types/pages/cashback';
import type { IRootReducer } from '@reducers/rootReducer';

import {
  cashbackGetAccountListStartAction,
  cashbackSetAccountAction,
} from '../actions/cashbackActions';
import type { IPageCashbackReducer } from '../pageCashbackReducer';
import {
  makeSelectCashbackSelectCurrent,
  makeSelectCashbackSelectHasMore,
  makeSelectCashbackSelectIsLoading,
  makeSelectCashbackSelectList,
} from '../selectors/cashbackSelectors';
import type { ICashbackGetAccountListStartPayload } from '../types/cashbackTypes';
import CashbackAccountSelectDropDownFilterButton from './CashbackAccountSelectDropDownFilterButton';

interface ICashbackAccountSelect {
  pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => void;
  translationCashback: IPageCashbackTranslation;
  translationCommon: ICommonTranslation;
}

export type TCashbackAccountSelect = ICashbackAccountSelect &
  IConnectedProps &
  IDispatchProps;

const CashbackAccountSelect = (props: TCashbackAccountSelect) => {
  const {
    accountSelectCurrent,
    accountSelectHasMore,
    accountSelectIsLoading,
    accountSelectList,
    translationCashback,
    translationCommon,

    getList,
    pushGtmAnalytic,
    setAccount,
  } = props;

  const [searchValue, setSearchValue] = useState('');

  /*
   * -------------------------------------------
   *          Select handlers
   * -------------------------------------------
   */

  // select account
  const onSelectHandler = useCallback(
    (selectedOption: IOptionItemProps) => {
      setAccount(selectedOption);
      pushGtmAnalytic({
        operation:
          GTMOperations.cashback.clickOnPersonalAccountNumberCashbackPage(
            selectedOption.label,
          ),
        withAccountType: true,
        withSusrId: true,
        withUserRole: true,
      });
    },
    [pushGtmAnalytic, setAccount],
  );

  // input search value

  const delayedStartSearch = debounce((searchValue: string) => {
    getList({ searchValue, start: true });
  }, DEBOUNCE_DEFAULT_DELAY);

  const onSearchChangeHandler = useCallback(
    (searchValue: string) => {
      setSearchValue(searchValue);
      delayedStartSearch(searchValue);
    },
    [delayedStartSearch],
  );

  // lazy load search options
  const onSelectScrollHandler = useCallback(() => {
    getList({ searchValue, start: false });
  }, [getList, searchValue]);

  /*
   * -------------------------------------------
   *          Render
   * -------------------------------------------
   */

  return (
    <Select
      widthAuto
      icon={arrowListDownLinkSmall}
      kind={EInputKind.normal}
      options={accountSelectList}
      placeholder=""
      renderButton={() => (
        <CashbackAccountSelectDropDownFilterButton
          translationCashback={translationCashback}
        />
      )}
      searchProps={{
        onSearchChange: onSearchChangeHandler,
        searchValue,
      }}
      selectedOption={accountSelectCurrent}
      translation={translationCommon}
      virtualListProps={{
        isLoading: accountSelectIsLoading,
        hasMore: accountSelectHasMore,
        onScrollEvent: onSelectScrollHandler,
        rowHeight: 40,
      }}
      onSelect={onSelectHandler}
    />
  );
};

export type IConnectedProps = Pick<
  IPageCashbackReducer,
  | 'accountSelectCurrent'
  | 'accountSelectHasMore'
  | 'accountSelectIsLoading'
  | 'accountSelectList'
>;

export const mapStateToProps = createStructuredSelector<
  IRootReducer,
  IConnectedProps
>({
  accountSelectCurrent: makeSelectCashbackSelectCurrent(),
  accountSelectHasMore: makeSelectCashbackSelectHasMore(),
  accountSelectIsLoading: makeSelectCashbackSelectIsLoading(),
  accountSelectList: makeSelectCashbackSelectList(),
});

export interface IDispatchProps {
  getList: (payload: ICashbackGetAccountListStartPayload) => void;
  setAccount: (payload: IOptionItemProps) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
  getList: (payload) => dispatch(cashbackGetAccountListStartAction(payload)),
  setAccount: (payload) => dispatch(cashbackSetAccountAction(payload)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CashbackAccountSelect);
